import { useMemo } from 'react'
import { useAuthStore } from '@/modules/auth/stores/authStore'
import {
  type PermissionKey,
  type AppRole,
  mapBackendRole,
  rolePermissions,
} from '@/modules/auth/permissions/permissions'

interface UsePermissionsReturn {
  /** Check if the current user has a specific permission */
  hasPermission: (permission: PermissionKey) => boolean
  /** Check if the current user has at least one of the given permissions */
  hasAnyPermission: (...permissions: PermissionKey[]) => boolean
  /** Check if the current user has all of the given permissions */
  hasAllPermissions: (...permissions: PermissionKey[]) => boolean
  /** The current user's frontend role (mapped from backend) */
  role: AppRole | null
  /** Whether the current user has the Admin role */
  isAdmin: boolean
}

/**
 * Hook to check permissions for the current authenticated user.
 *
 * @example
 * ```tsx
 * const { hasPermission, isAdmin } = usePermissions()
 *
 * if (hasPermission(Permission.CLIENTS_EXPORT)) {
 *   // show export button
 * }
 * ```
 */
export function usePermissions(): UsePermissionsReturn {
  const backendRole = useAuthStore((s) => s.user?.role)

  const appRole = useMemo<AppRole | null>(
    () => (backendRole ? mapBackendRole(backendRole) : null),
    [backendRole],
  )

  const permissions = useMemo<ReadonlySet<string>>(
    () => new Set(appRole ? rolePermissions[appRole] : []),
    [appRole],
  )

  const hasPermission = useMemo(
    () => (permission: PermissionKey): boolean => permissions.has(permission),
    [permissions],
  )

  const hasAnyPermission = useMemo(
    () =>
      (...perms: PermissionKey[]): boolean =>
        perms.some((p) => permissions.has(p)),
    [permissions],
  )

  const hasAllPermissions = useMemo(
    () =>
      (...perms: PermissionKey[]): boolean =>
        perms.every((p) => permissions.has(p)),
    [permissions],
  )

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    role: appRole,
    isAdmin: appRole === 'Admin',
  }
}
